
import React from "react";
import * as Hooks from "../../../hooks";
import { i18nManager } from "../../../i18n";
import { CacheManager, Schemes, Utils } from "../../../utils";
import { Elements } from "../../shared/Elements";
import * as ReactRouterDom from "react-router-dom";
import { ConfirmDialog, Siren } from "../../shared/ConfirmDialog";
import { Button, ButtonManageRef, Classname, Dropdown, FormControl, Scheme, TextInput, Popover, PopoverManageRef, AlertInterface, NumberInput, DOMHelper, Alignment, ObjectHelper, Transition, Animation, Debugger, DateHelper, Weekday, useScreenDimension, StringHelper } from "@rivtn/noseur";
import { ApiResponseData, BaseService, IMember, SanitizedResponse, EarningService, IMemberCurrencyBalance, IMemberPayoutMethod, IPlatformPayoutMethod, IExchangeRate, PayoutMethodDetailEntryType, PayoutSchedule, IMemberWithdrawalSetting, ChargesFeeService, FeeType, IChargeFee } from "../../../services";

export interface WithdrawalProps {
    profile: IMember;
}

function Withdrawal(props: WithdrawalProps) {

    const { profile } = props;
    const { width } = useScreenDimension();
    const labels = i18nManager.Labels.earnings;
    const [themeMode, _] = Hooks.useThemeMode();
    const navigate = ReactRouterDom.useNavigate();
    const earningService = EarningService.getInstance();
    const { currency = "NGN" } = ReactRouterDom.useParams();
    const currencySign = i18nManager.getCurrencySign(currency);
    const currencyName = i18nManager.getCurrencyName(currency);
    const memberPayoutMethodPopOverManageRef = React.useRef<PopoverManageRef>(null);
    const [activePayoutMethod, setActivePayoutMethod] = React.useState<IMemberPayoutMethod>();
    const [withdrawalSetting, setWithdrawalSetting] = React.useState<IMemberWithdrawalSetting>();
    const [searchParams, setSearchParams] = ReactRouterDom.useSearchParams(window.location.search);
    const [currencyBalance, setCurrencyBalance] = React.useState<IMemberCurrencyBalance | undefined>();
    const [memberPayoutMethods, setMemberPayoutMethods] = React.useState<ApiResponseData<IMemberPayoutMethod> | undefined>(undefined);

    React.useEffect(() => {
        fetchInformations();
        fetchWithdrawalSettings(true);
    }, [currency]);

    React.useEffect(() => {
        if (memberPayoutMethods?.content?.length && searchParams.has("edit_schedule")) {
            Utils.updateSearchParams("edit_schedule", undefined, setSearchParams);
            editSchedule();
        }
    }, [memberPayoutMethods]);

    return (<div className="earnings">
        <div className="header">
            <div className="l">
                <span className="t"><i className="fa fa-arrow-left" onClick={() => window.history.back()} /> {labels.earnings} - {currencyName} ({currency}) - {labels.withdrawal_settings.title}</span>
            </div>
            {(width > 800) && (<div className="queries">
                <Dropdown noIcon scheme={Schemes.OMNEHOST} formControlProps={{ className: "plan-dd", isFieldset: true, label: labels.transactions.currency_input.title, }}
                    selectedOptionIndex={(i18nManager.CountriesAndCurrencies?.findIndex((p) => p?.currency_code === currency) ?? 0)}
                    textInputProps={{ style: { paddingLeft: 15 } }}
                    options={i18nManager.CountriesAndCurrencies} optionMap={{
                        value: "currency_code",
                        label: "{currency_name} ({currency_code})",
                    }} onSelectOption={(option) => {
                        navigate(`/earnings/${option.currency_code}/withdrawal`);
                    }} />
            </div>)}
        </div>
        <div className="transactions withdrawal">
            <span>{labels.withdrawal_settings.available_balance}</span>
            <span style={{ fontSize: 20, fontWeight: "bold", margin: "3px 0px" }}>{currencyBalance ? `${currencySign} ${StringHelper.toPresentableMoneyValue(currencyBalance?.available_amount ?? 0)}` : (<i className="fa fa-spinner fa-spin" />)}</span>
            <span>{labels.withdrawal_settings.last_payment} {currencyBalance ? `${currencySign} ${StringHelper.toPresentableMoneyValue(currencyBalance?.last_withdrawn_amount ?? 0)}` : (<i className="fa fa-spinner fa-spin" />)}</span>
            <Button raised scheme={Scheme.SUCCESS} text={labels.withdrawal_settings.withdraw} style={{ marginTop: 10, padding: "10px 25px" }} onClick={() => InitiateWithdraw(currency, undefined, memberPayoutMethods?.content, fetchInformations)} />
            <span style={{ marginTop: 25, fontWeight: "bold", fontSize: 18 }}>{labels.withdrawal_settings.schedule}</span>
            {(!!withdrawalSetting) && (<span style={{ fontSize: 12, margin: "3px 0px" }}>{Utils.toSaneSentenceFormat(withdrawalSetting.schedule_interval)} {" "}
                ({labels.withdrawal_settings.next_on} {Utils.formatToShortDate(GetNextScheduledPayoutDate(withdrawalSetting.schedule_interval))}) {labels.withdrawal_settings.to} {" "}
                {withdrawalSetting.payout_method.platform_payout_method.name} ({withdrawalSetting.payout_method.platform_payout_method.payout_currency_code}) {Object.values(withdrawalSetting.payout_method.payout_details).reverse()[0]}</span>)}
            <Button raised scheme={Schemes.OMNEHOST} text={labels.withdrawal_settings.edit_schedule} style={{ marginTop: 10, padding: "10px 25px" }} onClick={() => editSchedule()} />
            <div className="payouts">
                <div className="header">
                    <span className="t">{labels.withdrawal_settings.payout.payout_method}</span>
                    <span className="a link" onClick={addPayoutMethod}><i className="fa fa-plus" />{labels.withdrawal_settings.payout.add_new_method}</span>
                </div>
                <div className="methods">
                    {!memberPayoutMethods && (Elements.LOADING_STATE)}
                    {(memberPayoutMethods && memberPayoutMethods.content && !memberPayoutMethods.content?.length) && (Elements.buildEmptyListView("fa fa-wallet", (<>
                        {labels.withdrawal_settings.payout.no_payout_parts.no_payout}{" "}
                        <span className="link" onClick={addPayoutMethod}>{labels.withdrawal_settings.payout.no_payout_parts.add_payout_method}</span>{" "}
                        {labels.withdrawal_settings.payout.no_payout_parts.to_be}
                    </>), { marginTop: 150 }))}
                    {memberPayoutMethods?.content?.map((memberPayoutMethod) => {

                        return (<div className="method" key={memberPayoutMethod.external_id}>
                            <img alt={memberPayoutMethod.platform_payout_method.name} src={memberPayoutMethod.extra_data.icon ?? memberPayoutMethod.platform_payout_method.logo_location} />
                            <div className="details">
                                <span className="t">
                                    {memberPayoutMethod.platform_payout_method.name} - {memberPayoutMethod.platform_payout_method.payout_currency_code}
                                    {memberPayoutMethod.is_default && (<span className="noseur-pill tf-pill-upcoming" style={{ marginLeft: 10 }}>{labels.withdrawal_settings.payout.preferred}</span>)}
                                </span>
                                {Object.entries(memberPayoutMethod.payout_details).reverse().map(([key, value], index) => (<div key={key}>
                                    <span style={{ fontWeight: (index === 0 ? "bold" : "normal") }} key={key}>{memberPayoutMethod.platform_payout_method.details.entries.find((f) => f.title === key)?.label ?? Utils.toSaneSentenceFormat(key)}</span>: <b>{value}</b>
                                </div>))}
                            </div>
                            <i className="fa fa-ellipsis" onClick={(e: any) => {
                                setActivePayoutMethod(memberPayoutMethod);
                                memberPayoutMethodPopOverManageRef.current?.show(e);
                            }} />
                        </div>);
                    })}
                </div>
            </div>
        </div>

        <Popover dismissOnClick manageRef={memberPayoutMethodPopOverManageRef} className={Classname.build("noseur-popover-b join-team-popover", themeMode)} style={{ marginTop: 55 }}>
            <span className={Classname.build({ "noseur-disabled": activePayoutMethod?.is_default })} onClick={setDefaultPayoutMethod}>{labels.withdrawal_settings.payout.popover.set_as_preferred}</span>
            <span style={{ color: "#FF7171" }} onClick={removePayoutMethod}>{labels.withdrawal_settings.payout.popover.remove}</span>
        </Popover>
    </div>);

    function fetchInformations() {
        setCurrencyBalance(undefined);
        earningService.getCurrencyBalance(currency).then(({ sanitized }: SanitizedResponse<IMemberCurrencyBalance>) => {
            setCurrencyBalance(sanitized);
        }).catch(BaseService.reportError);
    }

    function fetchWithdrawalSettings(fetPM?: boolean) {
        earningService.getWithdrawalSetting().then(({ sanitized }: SanitizedResponse<IMemberWithdrawalSetting>) => {
            setWithdrawalSetting(sanitized);
            if (fetPM) fetchPayoutMethods();
        }).catch(BaseService.reportError);
    }

    function fetchPayoutMethods() {
        setMemberPayoutMethods(undefined);
        earningService.queryMemberPayoutMethods({
            currency_code: currency,
        }).then(({ apiResponse }: SanitizedResponse<any>) => {
            setMemberPayoutMethods(apiResponse.data!);
        }).catch(BaseService.reportError);
    }

    function setDefaultPayoutMethod() {
        if (!activePayoutMethod) return;
        const dialogLabels = labels.withdrawal_settings.dialog.add_payout;

        earningService.setDefaultMemberPayoutMethod(activePayoutMethod.external_id).then(({ }: SanitizedResponse<any>) => {
            fetchPayoutMethods();
            Siren.alert(dialogLabels.default_successful);
        }).catch(BaseService.reportError);
    }

    function removePayoutMethod() {
        if (!activePayoutMethod) return;
        const dialogLabels = labels.withdrawal_settings.dialog.remove_payout_method;
        const dialogErrorRef = { current: null } as any;

        const dialog = ConfirmDialog({
            closable: true,
            title: dialogLabels.title,
            confirmScheme: Scheme.DANGER,
            style: { maxWidth: 500 },
            desc: dialogLabels.desc.replaceAll("${label}", activePayoutMethod.platform_payout_method.name + " - " + Object.values(activePayoutMethod.payout_details).reverse()[0]),
            content: (<div ref={dialogErrorRef} className="error" style={{ alignSelf: "flex-start" }}> </div>),
            onConfirm: (button?: ButtonManageRef) => {
                button?.setLoadingState(true);
                dialogErrorRef.current!.innerText = " ";
                earningService.deleteMemberPayoutMethod(activePayoutMethod.external_id).then(({ }: SanitizedResponse<any>) => {
                    dialog.hide(() => {
                        fetchPayoutMethods();
                        Siren.alert(dialogLabels.remove_successful);
                    });
                }).catch(({ errorMessage }) => {
                    dialogErrorRef.current!.innerText = errorMessage;
                }).finally(() => {
                    button?.setLoadingState(false);
                });
                return false;
            }
        });
        dialog.show();
    }

    function removeWithdrawalSettings() {
        if (!withdrawalSetting) return;
        const dialogLabels = labels.withdrawal_settings.dialog.remove_withdrawal_settings;
        const dialogErrorRef = { current: null } as any;

        const dialog = ConfirmDialog({
            closable: true,
            style: { maxWidth: 500 },
            title: dialogLabels.title,
            confirmScheme: Scheme.DANGER,
            onCancel: () => editSchedule(),
            desc: dialogLabels.desc.replaceAll("${interval}", Utils.toSaneSentenceFormat(withdrawalSetting.schedule_interval)),
            content: (<div ref={dialogErrorRef} className="error" style={{ alignSelf: "flex-start" }}> </div>),
            onConfirm: (button?: ButtonManageRef) => {
                button?.setLoadingState(true);
                dialogErrorRef.current!.innerText = " ";
                earningService.deleteWithdrawalSetting().then(({ }: SanitizedResponse<any>) => {
                    dialog.hide(() => {
                        fetchWithdrawalSettings();
                        Siren.alert(dialogLabels.remove_successful);
                    });
                }).catch(({ errorMessage }) => {
                    dialogErrorRef.current!.innerText = errorMessage;
                }).finally(() => {
                    button?.setLoadingState(false);
                });
                return false;
            },
        });
        dialog.show();
    }

    function editSchedule(selectedPayoutMethod?: IMemberPayoutMethod) {
        const dialogErrorRef = { current: null } as any;
        const earningService = EarningService.getInstance();
        const dialogScheduleDateRef = { current: null } as any;
        const currencySign = i18nManager.getCurrencySign(currency);
        const labels = i18nManager.Labels.earnings.withdrawal_settings.dialog.edit_schedule;
        if (!selectedPayoutMethod) selectedPayoutMethod = withdrawalSetting?.payout_method ?? memberPayoutMethods?.content?.find((p) => p.is_default) ?? memberPayoutMethods?.content?.[0];
        let payload: IMemberWithdrawalSetting = {
            currency_code: currency,
            min_amount: withdrawalSetting?.min_amount as any,
            reserve_amount: withdrawalSetting?.reserve_amount as any,
            schedule_interval: withdrawalSetting?.schedule_interval as any,
            member_payout_method_id: selectedPayoutMethod?.external_id as any,
        } as any;
        const scheduleBalances = [
            1000000000,
            2000000000,
            5000000000,
            10000000000,
            100000000000,
        ];
        const reserveBalances = [
            0,
            100000000,
            1000000000,
            3000000000,
            10000000000,
        ];

        const dialog = ConfirmDialog({
            closable: true,
            title: labels.title,
            style: width > 800 ? { minWidth: 500, maxWidth: 500 } : { minWidth: "90%", maxWidth: "90%" },
            content: (<div className="withdraw-diag" style={{ marginTop: 15 }}>
                <span className="pm">{i18nManager.Labels.earnings.withdrawal_settings.dialog.withdraw.payout_method}</span>
                <div className="payouts wd">
                    <div className="methods">
                        <div className="method" key={selectedPayoutMethod?.external_id}>
                            <img alt={selectedPayoutMethod?.platform_payout_method.name} src={selectedPayoutMethod?.extra_data.icon ?? selectedPayoutMethod?.platform_payout_method.logo_location} />
                            <div className="details">
                                <span className="t">
                                    {selectedPayoutMethod?.platform_payout_method.name} - {selectedPayoutMethod?.platform_payout_method.payout_currency_code}
                                </span>
                                {Object.entries(selectedPayoutMethod?.payout_details ?? {}).reverse().map(([key, value], index) => (<div>
                                    <span style={{ fontWeight: (index === 0 ? "bold" : "normal") }} key={key}>{selectedPayoutMethod?.platform_payout_method.details.entries.find((f) => f.title === key)?.label ?? Utils.toSaneSentenceFormat(key)}</span>: <b>{value}</b>
                                </div>))}
                            </div>
                            <span className="link" onClick={() => dialog.destroy(() => ChangePayoutMethod(currency, selectedPayoutMethod!, (c, pm) => editSchedule(pm)))}>{i18nManager.Labels.earnings.withdrawal_settings.dialog.withdraw.change}</span>
                        </div>
                    </div>
                </div>
                <hr style={{ marginTop: 0 }} />
                <span style={{ marginTop: 10, }}>
                    <b>{labels.withdrawal_fee}</b> <span>{selectedPayoutMethod?.platform_payout_method?.withdrawal_fee_percentage}% - {labels.min_of} {currencySign} {StringHelper.toPresentableMoneyValue(selectedPayoutMethod?.platform_payout_method?.withdrawal_minimum_amount ?? 0)}</span>
                </span>
                <span style={{ marginTop: 20, fontWeight: "bold", fontSize: 18 }}>{labels.preferred_payment_schedule}</span>
                <span style={{ fontSize: 13, marginTop: 5 }}>{labels.you_can_withdraw_any_time} <a target="_blank" href={labels.learn_more.location} style={{ fontSize: 13 }}>{labels.learn_more.label}</a></span>
                <Dropdown noIcon fill scheme={Schemes.OMNEHOST} formControlProps={{ className: "plan-dd", isFieldset: true, label: labels.schedule, style: { marginTop: 10 } }}
                    textInputProps={{ style: { paddingLeft: 15 } }} options={Object.entries(PayoutSchedule)?.map(([value, label]) => {
                        return {
                            value,
                            label: Utils.toSaneSentenceFormat(label),
                        };
                    })} selectedOptionIndex={Object.values(PayoutSchedule).findIndex((p) => p === withdrawalSetting?.schedule_interval)} onSelectOption={(option) => {
                        dialogErrorRef.current!.innerText = " ";
                        payload.schedule_interval = option.value;
                        dialogScheduleDateRef.current!.innerText = Utils.formatToFineDate1(GetNextScheduledPayoutDate(option.value), false, true);
                    }} />
                <Dropdown noIcon fill scheme={Schemes.OMNEHOST} formControlProps={{ className: "plan-dd", isFieldset: true, label: labels.when_balance_is, style: { marginTop: 5 } }}
                    textInputProps={{ style: { paddingLeft: 15 } }} options={scheduleBalances?.map((value) => {
                        const valueAmount = StringHelper.toPresentableMoneyValue(value);
                        return {
                            value,
                            label: `${currencySign} ${valueAmount} ${labels.or_more}`,
                        };
                    })} selectedOptionIndex={scheduleBalances.indexOf(withdrawalSetting?.min_amount ?? -1)} onSelectOption={(option) => {
                        dialogErrorRef.current!.innerText = " ";
                        payload.min_amount = parseInt(option.value);
                    }} />
                <Dropdown noIcon fill scheme={Schemes.OMNEHOST} formControlProps={{ className: "plan-dd", isFieldset: true, label: labels.leave_reserve_of, style: { marginTop: 5 } }}
                    textInputProps={{ style: { paddingLeft: 15 } }} options={reserveBalances?.map((value) => {
                        const valueAmount = StringHelper.toPresentableMoneyValue(value);
                        return {
                            value,
                            label: `${currencySign} ${valueAmount}`,
                        };
                    })} selectedOptionIndex={reserveBalances.indexOf(withdrawalSetting?.reserve_amount ?? -1)} onSelectOption={(option) => {
                        dialogErrorRef.current!.innerText = " ";
                        payload.reserve_amount = parseInt(option.value);
                    }} />
                <span style={{ marginTop: 20 }}><b>{labels.next_payment_based_on_schedule}</b> <span ref={(r) => {
                    ObjectHelper.resolveRef(dialogScheduleDateRef, r);
                    if (r && withdrawalSetting?.schedule_interval) r!.innerText = Utils.formatToFineDate1(GetNextScheduledPayoutDate(withdrawalSetting?.schedule_interval), false, true);
                }} /></span>
                <div ref={dialogErrorRef} className="error" style={{ alignSelf: "flex-start" }}> </div>
            </div>),
            footerElements: (withdrawalSetting ? (<Button textOnly scheme={Scheme.DANGER} text={labels.remove} loadingProps={Elements.BUTTON_LOADING_PROPS} onClick={(e) => {
                dialog.hide(removeWithdrawalSettings);
            }} />) : undefined),
            onConfirm: (button?: ButtonManageRef) => {
                dialogErrorRef.current!.innerText = " ";
                if (!payload.schedule_interval || !payload.min_amount || !payload.member_payout_method_id) {
                    dialogErrorRef.current!.innerText = labels.select_all_fields;
                    return false;
                }
                button?.setLoadingState(true);
                earningService.updateWithdrawalSetting(payload).then(({ }: SanitizedResponse<any>) => {
                    dialog.hide(() => {
                        fetchWithdrawalSettings();
                        Siren.alert(labels.add_successful);
                    });
                }).catch(({ errorMessage }) => {
                    dialogErrorRef.current!.innerText = errorMessage;
                }).finally(() => {
                    button?.setLoadingState(false);
                });
                return false;
            }
        });
        dialog.show();
    }

    function addPayoutMethod() {
        const dialogErrorRef = { current: null } as any;
        const earningService = EarningService.getInstance();
        const labels = i18nManager.Labels.earnings.withdrawal_settings.dialog.add_payout;

        const dialog = ConfirmDialog({
            closable: false,
            title: labels.title,
            confirmTextOnly: true,
            confirmScheme: Scheme.DANGER,
            footerAlignment: Alignment.RIGHT,
            confirmLabel: i18nManager.Labels.common.close,
            onLoading: async (alert: AlertInterface) => {
                earningService.queryPayoutMethods({ currency_code: currency }).then(({ content }: SanitizedResponse<IPlatformPayoutMethod>) => {
                    alert.doneLoading(() => {
                        alert.update({
                            __pass_through__: true,
                            style: width > 800 ? { minWidth: 500, maxWidth: 600 } : { minWidth: "90%", maxWidth: "90%" },
                            content: (<div className="add-payout-m-diag">
                                <span className="st">{labels.recommended_for} {i18nManager.getCurrencyName(currency)}</span>
                                <div className="pay-meths">
                                    {content?.filter((c) => c.recommended).map((payoutMethod) => {

                                        return (<React.Fragment key={payoutMethod.external_id}>
                                            <hr style={{ marginTop: 10 }} />
                                            <div className="pay-meth" key={payoutMethod.external_id}>
                                                <img alt={payoutMethod.name} src={payoutMethod.logo_location} />
                                                <div className="details">
                                                    <span className="name">{payoutMethod.name} ({payoutMethod.payout_currency_code})</span>
                                                    <ul>
                                                        <li>{payoutMethod.withdrawal_fee_percentage}% {labels.per_withdrawal} <i className="fa fa-info-circle" style={{ cursor: "pointer" }} /></li>
                                                        {payoutMethod?.information?.map((info) => (<li key={info} dangerouslySetInnerHTML={{ __html: info }} />))}
                                                    </ul>
                                                </div>
                                                <Button raised rounded scheme={Schemes.OMNEHOST} style={{ borderRadius: 20, padding: "10px 35px" }} text={labels.add} onClick={() => {
                                                    dialog.hide(() => addPayoutMethodDetails(payoutMethod));
                                                }} />
                                            </div>
                                        </React.Fragment>);
                                    })}
                                </div>
                                <span className="st">{labels.other_methods}</span>
                                <div className="pay-meths">
                                    {content?.filter((c) => !c.recommended).map((payoutMethod) => {
                                        return (<React.Fragment key={payoutMethod.external_id}>
                                            <hr style={{ marginTop: 10 }} />
                                            <div className="pay-meth" key={payoutMethod.external_id}>
                                                <img alt={payoutMethod.name} src={payoutMethod.logo_location} />
                                                <div className="details">
                                                    <span className="name">{payoutMethod.name} ({payoutMethod.payout_currency_code})</span>
                                                    <ul>
                                                        <li>{payoutMethod.withdrawal_fee_percentage}% {labels.per_withdrawal} <i className="fa fa-info-circle" style={{ cursor: "pointer" }} /></li>
                                                        {payoutMethod?.information?.map((info) => (<li key={info} dangerouslySetInnerHTML={{ __html: info }} />))}
                                                    </ul>
                                                </div>
                                                <Button raised rounded scheme={Schemes.OMNEHOST} style={{ borderRadius: 20, padding: "10px 35px" }} text={labels.add} onClick={() => {
                                                    dialog.hide(() => addPayoutMethodDetails(payoutMethod));
                                                }} />
                                            </div>
                                        </React.Fragment>);
                                    })}
                                </div>
                                <div ref={dialogErrorRef} className="error" style={{ alignSelf: "flex-start" }}> </div>
                            </div>),
                        } as any);
                    });
                }).catch((error: SanitizedResponse<any>) => {
                    alert.hide(() => BaseService.reportError(error));
                });
                return false;
            },
        });
        dialog.show();
    }

    function addPayoutMethodDetails(payoutMethod: IPlatformPayoutMethod) {
        const dialogErrorRef = { current: null } as any;
        const earningService = EarningService.getInstance();
        const labels = i18nManager.Labels.earnings.withdrawal_settings.dialog.add_payout;
        const payload: any = { payout_details: {}, currency_code: currency, platform_payout_method_id: payoutMethod.external_id };

        const dialog = ConfirmDialog({
            closable: true,
            onCancel: addPayoutMethod,
            title: labels.enter_payout_details,
            cancelLabel: i18nManager.Labels.common.back,
            confirmLabel: i18nManager.Labels.common.save,
            onLoading: async (alert: AlertInterface) => {
                earningService.getExchangeRate(currency, payoutMethod.payout_currency_code).then(({ sanitized }: SanitizedResponse<IExchangeRate>) => {
                    const hasRate = (sanitized.exchange_rate / Utils.AMOUNT_MICRO_AMOUNT) > 1;
                    alert.doneLoading(() => {
                        alert.update({
                            __pass_through__: true,
                            style: width > 800 ? { minWidth: 500, maxWidth: 600 } : { minWidth: "90%", maxWidth: "90%" },
                            content: (<div className="add-payout-m-diag">
                                <div className="pay-meths">
                                    <hr style={{ marginTop: 10 }} />
                                    <div className="pay-meth" key={payoutMethod.external_id}>
                                        <img alt={payoutMethod.name} src={payoutMethod.logo_location} />
                                        <div className="details">
                                            <span className="name">{payoutMethod.name} ({payoutMethod.payout_currency_code})</span>
                                            <ul>
                                                <li>{payoutMethod.withdrawal_fee_percentage}% {labels.per_withdrawal} <i className="fa fa-info-circle" style={{ cursor: "pointer" }} /></li>
                                                {payoutMethod?.information?.map((info) => (<li key={info} dangerouslySetInnerHTML={{ __html: info }} />))}
                                            </ul>
                                        </div>
                                    </div>
                                    <hr style={{ marginTop: 0 }} />
                                </div>
                                {(hasRate) && (<>
                                    <span style={{ fontWeight: "bold" }}>{labels.currency}</span>
                                    <span>{i18nManager.getCurrencyName(currency)} ({currency})</span>
                                    <span>{labels.current_exchange_rate} {StringHelper.toPresentableMoneyValue(sanitized.exchange_rate)} {payoutMethod.payout_currency_code}/{currency}</span>
                                    <span>{Utils.formatToFineDate(new Date())}</span>
                                </>)}
                                <span style={{ fontWeight: "bold", marginTop: (hasRate ? 10 : 0) }}>{labels.withdrawal_fee}</span>
                                <span>{payoutMethod.withdrawal_fee_percentage} %, {i18nManager.Labels.earnings.withdrawal_settings.dialog.edit_schedule.min_of} {currencySign} {StringHelper.toPresentableMoneyValue(payoutMethod.withdrawal_minimum_amount)} {labels.per_withdrawal}</span>
                                <hr />
                                <b>{labels.details_translation[payoutMethod.details.title] ?? Utils.toSaneSentenceFormat(payoutMethod.details.title)}</b>
                                <hr style={{ marginBottom: 0 }} />
                                {payoutMethod.details.entries?.map((entry) => {
                                    const title = labels.details_translation[entry.title] ?? Utils.toSaneSentenceFormat(entry.title);

                                    if (entry.type === PayoutMethodDetailEntryType.OPTIONS) {
                                        return (<Dropdown key={entry.title} fill scheme={Schemes.OMNEHOST} formControlProps={{ className: "plan-dd", isFieldset: true, label: title, style: { marginTop: 8 } }}
                                            textInputProps={{ style: { paddingLeft: 15 } }} options={entry.options} onSelectOption={(option) => {
                                                dialogErrorRef.current!.innerText = " ";
                                                if (option.icon) {
                                                    payload.extra_data = { ...payload.extra_data, icon: option.icon };
                                                }
                                                payload.payout_details[entry.title] = option.label ?? option.value;
                                            }} />);
                                    }
                                    return (<FormControl isFieldset fill label={title} style={{ marginTop: 15 }} key={entry.title} >
                                        {React.createElement((entry.type === PayoutMethodDetailEntryType.NUMBER ? NumberInput : TextInput), {
                                            fill: true,
                                            key: entry.title,
                                            onInputComplete: (v: string) => {
                                                dialogErrorRef.current!.innerText = " ";
                                                payload.payout_details[entry.title] = v;
                                            },
                                        })}
                                    </FormControl>);
                                })}
                                <div ref={dialogErrorRef} className="error" style={{ alignSelf: "flex-start" }}> </div>
                            </div>),
                        } as any);
                    });
                }).catch((error: SanitizedResponse<any>) => {
                    alert.hide(() => BaseService.reportError(error));
                });
                return false;
            },
            onConfirm: (button?: ButtonManageRef) => {
                dialogErrorRef.current!.innerText = " ";
                for (const entry of payoutMethod.details.entries) {
                    if (!payload.payout_details[entry.title]) {
                        dialogErrorRef.current!.innerText = labels.missing_a_payout_field;
                        return false;
                    }
                }
                button?.setLoadingState(true);
                earningService.createMemberPayoutMethod(payload).then(({ }: SanitizedResponse<any>) => {
                    dialog.hide(() => {
                        fetchPayoutMethods();
                        Siren.alert(labels.add_successful);
                    });
                }).catch(({ errorMessage }) => {
                    dialogErrorRef.current!.innerText = errorMessage;
                }).finally(() => {
                    button?.setLoadingState(false);
                });
                return false;
            }
        });
        dialog.show();
    }

}

function ChangePayoutMethod(currency: string, selectedPayoutMethod: IMemberPayoutMethod, cb: (currency: string, pm: IMemberPayoutMethod) => void) {
    const dialogErrorRef = { current: null } as any;
    const width = document.documentElement.clientWidth;
    const ogSelectedPayoutMethod = selectedPayoutMethod;
    const earningService = EarningService.getInstance();
    const labels = i18nManager.Labels.earnings.withdrawal_settings.dialog.select_payout_method;

    const dialog = ConfirmDialog({
        closable: true,
        title: labels.title,
        confirmLabel: labels.use_selected,
        contentStyle: { overflow: "auto" },
        cancelLabel: i18nManager.Labels.common.back,
        onLoading: async (alert: AlertInterface) => {
            earningService.queryMemberPayoutMethods({ currency_code: currency }).then(({ apiResponse }: SanitizedResponse<IMemberPayoutMethod>) => {
                alert.doneLoading(() => {
                    alert.update({
                        __pass_through__: true,
                        style: width > 800 ? { minWidth: 500, maxWidth: 500, maxHeight: 600 } : { minWidth: "90%", maxWidth: "90%" },
                        content: (<div className="withdraw-diag">
                            <div className="payouts">
                                <div className="methods">
                                    {apiResponse.data?.content?.map((memberPayoutMethod) => {
                                        const elementId = `cpm-pm-${memberPayoutMethod.external_id}`;

                                        return (<div id={elementId} className={Classname.build("method cpm cpm-pmethod", { "active": selectedPayoutMethod?.external_id === memberPayoutMethod.external_id })} key={memberPayoutMethod.external_id} onClick={() => {
                                            if (selectedPayoutMethod?.external_id === memberPayoutMethod.external_id) return;
                                            selectedPayoutMethod = memberPayoutMethod;
                                            DOMHelper.removeClassFromElementsByClassname("cpm-pmethod", "active");
                                            DOMHelper.addClassToElementById(elementId, "active");
                                        }} ref={(r) => {
                                            if (r && selectedPayoutMethod?.external_id === memberPayoutMethod.external_id) r.scrollIntoView();
                                        }}>
                                            <img alt={memberPayoutMethod.platform_payout_method.name} src={memberPayoutMethod.extra_data.icon ?? memberPayoutMethod.platform_payout_method.logo_location} />
                                            <div className="details">
                                                <span className="t">
                                                    {memberPayoutMethod.platform_payout_method.name} - {memberPayoutMethod.platform_payout_method.payout_currency_code}
                                                    {memberPayoutMethod.is_default && (<span className="noseur-pill tf-pill-upcoming" style={{ marginLeft: 10 }}>{i18nManager.Labels.earnings.withdrawal_settings.payout.preferred}</span>)}
                                                </span>
                                                {Object.entries(memberPayoutMethod.payout_details).reverse().map(([key, value], index) => (<div key={key}>
                                                    <span style={{ fontWeight: (index === 0 ? "bold" : "normal") }} key={key}>{memberPayoutMethod.platform_payout_method.details.entries.find((f) => f.title === key)?.label ?? Utils.toSaneSentenceFormat(key)}</span>: <b>{value}</b>
                                                </div>))}
                                            </div>
                                        </div>);
                                    })}
                                </div>
                            </div>
                            <div ref={dialogErrorRef} className="error" style={{ alignSelf: "flex-start" }}> </div>
                        </div>),
                    } as any);
                });
            }).catch((error: SanitizedResponse<any>) => {
                alert.hide(() => BaseService.reportError(error));
            });
            return false;
        },
        onCancel: () => {
            dialog.destroy(() => cb(currency, ogSelectedPayoutMethod));
        },
        onConfirm: () => {
            dialog.destroy(() => cb(currency, selectedPayoutMethod));
            return true;
        }
    });
    dialog.show();
}

export function GetNextScheduledPayoutDate(interval: PayoutSchedule) {
    if (interval === PayoutSchedule.QUARTERLY) {
        return DateHelper.getNextQuarterStart();
    } else if (interval === PayoutSchedule.LAST_FRIDAY_OF_EVERY_MONTH) {
        return DateHelper.getLastWeekdayOfMonth(Weekday.FRIDAY);
    } else if (interval === PayoutSchedule.EVERY_2_WEEKS) {
        return DateHelper.getNextTwoWeekDayFrom(new Date(DateHelper.now().getFullYear() + "-01-01"), Weekday.FRIDAY);
    }
    return DateHelper.getNextWeekDay(Weekday.FRIDAY);
}

export async function InitiateWithdraw(currency: string, selectedPayoutMethod?: IMemberPayoutMethod, memberPayoutMethods?: IMemberPayoutMethod[], onDone?: () => void) {
    let amountToWithdraw: number | undefined;
    let platformChargeAmount: number | undefined;
    let finalAmountToWithdraw: number | undefined;
    const dialogErrorRef = { current: null } as any;
    let platformChargePercentage: number | undefined;
    const width = document.documentElement.clientWidth;
    const earningService = EarningService.getInstance();
    const finalPayoutAmountRef = { current: null } as any;
    let currencyBalance: IMemberCurrencyBalance | undefined;
    const platformChargeAmountRef = { current: null } as any;
    const chargesFeeService = ChargesFeeService.getInstance();
    const currencySign = i18nManager.getCurrencySign(currency);
    const labels = i18nManager.Labels.earnings.withdrawal_settings.dialog.withdraw;
    if (!memberPayoutMethods) memberPayoutMethods = (await earningService.queryMemberPayoutMethods({}))?.content;

    const dialog = ConfirmDialog({
        closable: true,
        title: labels.title,
        onLoading: async (alert: AlertInterface) => {
            earningService.getCurrencyBalance(currency).then(async ({ sanitized }: SanitizedResponse<IMemberCurrencyBalance>) => {
                currencyBalance = sanitized;
                if (!selectedPayoutMethod) selectedPayoutMethod = memberPayoutMethods?.find((p) => p.is_default) ?? memberPayoutMethods?.[0];
                let chargeFee: IChargeFee = {
                    external_id: "001",
                    amounts_min_caps: {},
                    amounts_max_caps: {},
                    amount_type: "PERCENTAGE",
                    type: FeeType.MEMBER_WITHDRAWAL,
                    amounts: {
                        [currency]: selectedPayoutMethod?.platform_payout_method?.withdrawal_fee_percentage ?? 0
                    },
                };
                try {
                    chargeFee = (await chargesFeeService.get({
                        actor_type: "MEMBER",
                        type: FeeType.MEMBER_WITHDRAWAL,
                        actor_id: CacheManager.get(CacheManager.PROFILE_KEY).external_id,
                    })).sanitized;
                } catch (err) {
                    Debugger.log("EARNING.WITHDRAWAL", "error fetching charge fee", err);
                }
                Debugger.log("EARNING.WITHDRAWAL", "charge fee", chargeFee);
                const minimumAmount = selectedPayoutMethod?.platform_payout_method?.withdrawal_minimum_amount ?? 1000000000;
                const maximumAmount = selectedPayoutMethod?.platform_payout_method?.withdrawal_maximum_amount ?? 1000000000000;
                alert.doneLoading(() => {
                    alert.update({
                        __pass_through__: true,
                        style: width > 800 ? { minWidth: 500, maxWidth: 500 } : { minWidth: "90%", maxWidth: "90%" },
                        content: (<div className="withdraw-diag">
                            <span className="pm">{labels.payout_method}</span>
                            <div className="payouts wd">
                                <div className="methods">
                                    <div className="method" key={selectedPayoutMethod?.external_id}>
                                        <img alt={selectedPayoutMethod?.platform_payout_method.name} src={selectedPayoutMethod?.extra_data.icon ?? selectedPayoutMethod?.platform_payout_method.logo_location} />
                                        <div className="details">
                                            <span className="t">
                                                {selectedPayoutMethod?.platform_payout_method.name} - {selectedPayoutMethod?.platform_payout_method.payout_currency_code}
                                            </span>
                                            {Object.entries(selectedPayoutMethod?.payout_details ?? {}).reverse().map(([key, value], index) => (<div>
                                                <span style={{ fontWeight: (index === 0 ? "bold" : "normal") }} key={key + index}>{selectedPayoutMethod?.platform_payout_method.details.entries.find((f) => f.title === key)?.label ?? Utils.toSaneSentenceFormat(key)}</span>: <b>{value}</b>
                                            </div>))}
                                        </div>
                                        <span className="link" onClick={() => dialog.destroy(() => ChangePayoutMethod(currency, selectedPayoutMethod!, (c, pm) => InitiateWithdraw(c, pm, memberPayoutMethods, onDone)))}>{i18nManager.Labels.earnings.withdrawal_settings.dialog.withdraw.change}</span>
                                    </div>
                                </div>
                            </div>
                            <hr style={{ marginTop: 0 }} />
                            <FormControl isFieldset fill label={`${labels.amount} (${labels.max} ${currencySign} ${StringHelper.toPresentableMoneyValue(currencyBalance?.available_amount!)})`} style={{ marginTop: 5 }}
                                leftContent={<span>{currencySign}</span>}>
                                <NumberInput fill onInput={async ({ target: { value } }: any) => {
                                    amountToWithdraw = undefined;
                                    platformChargeAmount = undefined;
                                    finalAmountToWithdraw = undefined;
                                    platformChargePercentage = undefined;
                                    dialogErrorRef.current!.innerText = " ";
                                    if (!finalPayoutAmountRef?.current) return;
                                    finalPayoutAmountRef.current.innerText = "0.00";
                                    platformChargeAmountRef.current.innerText = "0.00";
                                    value = parseFloat(value);
                                    const amountInMicro = value * 1000000;
                                    if (amountInMicro < minimumAmount) {
                                        dialogErrorRef.current!.innerText = labels.amount_lower.replaceAll("${min_amount}", `${currencySign} ${StringHelper.toPresentableMoneyValue(minimumAmount)}`);
                                        return;
                                    }
                                    if (amountInMicro > maximumAmount) {
                                        dialogErrorRef.current!.innerText = labels.amount_greater.replaceAll("${max_amount}", `${currencySign} ${StringHelper.toPresentableMoneyValue(maximumAmount)}`);
                                        return;
                                    }
                                    if (amountInMicro > currencyBalance?.available_amount!) {
                                        dialogErrorRef.current!.innerText = labels.amount_greater_available;
                                        return;
                                    }
                                    const chargeFeeAmount = chargesFeeService.getFlatAmount(chargeFee, amountInMicro, currency);
                                    amountToWithdraw = amountInMicro;
                                    platformChargeAmount = chargeFeeAmount;
                                    finalAmountToWithdraw = amountInMicro - chargeFeeAmount;
                                    finalPayoutAmountRef.current.innerText = StringHelper.toPresentableMoneyValue(finalAmountToWithdraw);
                                    platformChargePercentage = chargesFeeService.getPercentageCharged(amountToWithdraw, finalAmountToWithdraw);
                                    platformChargeAmountRef.current.innerText = StringHelper.toPresentableMoneyValue(chargeFeeAmount) + (platformChargePercentage ? (` (${platformChargePercentage.toFixed(2)} %)`) : "");
                                    Debugger.log("EARNING.WITHDRAWAL", "calculated amounts", "Micro", amountInMicro, "ToWithdraw", amountToWithdraw, "Final", finalAmountToWithdraw, "ChargeAmount", chargeFeeAmount);
                                }} />
                            </FormControl>
                            <span style={{ marginTop: 20, }}>
                                <b>{labels.withdrawal_fee}</b> {currencySign} <span ref={platformChargeAmountRef}>0.00</span>
                            </span>
                            <span style={{ marginTop: 6, }}>
                                <b>{labels.you_will_receive}</b> {currencySign} <span ref={finalPayoutAmountRef}>0.00</span>
                            </span>
                            <hr />
                            <div ref={dialogErrorRef} className="error" style={{ alignSelf: "flex-start" }}> </div>
                        </div>),
                    } as any);
                });
            }).catch((error: SanitizedResponse<any>) => {
                alert.hide(() => BaseService.reportError(error));
            });
            return false;
        },
        onConfirm: () => {
            dialogErrorRef.current!.innerText = " ";
            if (!amountToWithdraw || !currencyBalance || !selectedPayoutMethod || ((amountToWithdraw - (platformChargeAmount ?? 0)) < 0)) {
                dialogErrorRef.current!.innerText = labels.invalid_amount;
                return false;
            }
            ConfirmInitiateWithdraw(currency, selectedPayoutMethod!, {
                amount: amountToWithdraw,
                finalAmount: finalAmountToWithdraw!,
                platform_charge: platformChargeAmount!,
                platform_charge_percentage: platformChargePercentage
            }, memberPayoutMethods, onDone);
            return true;
        }
    });
    dialog.show();
}

function ConfirmInitiateWithdraw(currency: string, selectedPayoutMethod: IMemberPayoutMethod, amounts: { amount: number; finalAmount: number; platform_charge: number; platform_charge_percentage?: number; }, memberPayoutMethods?: IMemberPayoutMethod[], onDone?: () => void) {
    const dialogErrorRef = { current: null } as any;
    const width = document.documentElement.clientWidth;
    const earningService = EarningService.getInstance();
    const currencySign = i18nManager.getCurrencySign(currency);
    const labels = i18nManager.Labels.earnings.withdrawal_settings.dialog.withdraw;
    const payload: any = {
        amount: amounts.amount,
        currency_code: currency,
        member_payout_method_id: selectedPayoutMethod.external_id
    };

    async function sendConfirmationToken() {
        if (dialogErrorRef.current) dialogErrorRef.current!.innerText = " ";
        try {
            const result = (await earningService.requestWithdrawalToken(payload))?.sanitized;
            payload.factor_id = result.email_factor_id;
        } catch (err: any) {
            BaseService.reportError(err);
            if (dialogErrorRef.current) dialogErrorRef.current!.innerText = `${labels.token_error} ${err}`;
        }
    }

    const dialog = ConfirmDialog({
        closable: true,
        title: labels.title,
        cancelLabel: i18nManager.Labels.common.back,
        style: width > 800 ? { minWidth: 500, maxWidth: 500 } : { minWidth: "90%", maxWidth: "90%" },
        content: (<div className="withdraw-diag" style={{ marginTop: 20 }}>
            <span className="pm">{labels.payout_method}</span>
            <div className="payouts wd">
                <div className="methods">
                    <div className="method" key={selectedPayoutMethod?.external_id}>
                        <img alt={selectedPayoutMethod?.platform_payout_method.name} src={selectedPayoutMethod?.extra_data.icon ?? selectedPayoutMethod?.platform_payout_method.logo_location} />
                        <div className="details">
                            <span className="t">
                                {selectedPayoutMethod?.platform_payout_method.name} - {selectedPayoutMethod?.platform_payout_method.payout_currency_code}
                            </span>
                            {Object.entries(selectedPayoutMethod?.payout_details ?? {}).reverse().map(([key, value], index) => (<div>
                                <span style={{ fontWeight: (index === 0 ? "bold" : "normal") }} key={key}>{selectedPayoutMethod?.platform_payout_method.details.entries.find((f) => f.title === key)?.label ?? Utils.toSaneSentenceFormat(key)}</span>: <b>{value}</b>
                            </div>))}
                        </div>
                    </div>
                </div>
            </div>
            <hr style={{ marginTop: 0 }} />
            <span style={{ marginTop: 6, }}>
                <b>{labels.amount}</b> {currencySign} {StringHelper.toPresentableMoneyValue(amounts.amount)}
            </span>
            <span style={{ marginTop: 10, }}>
                <b>{labels.withdrawal_fee}</b> {currencySign} {StringHelper.toPresentableMoneyValue(amounts.platform_charge)} {amounts.platform_charge_percentage ? (`(${amounts.platform_charge_percentage} %)`) : ""}
            </span>
            <span style={{ marginTop: 10, }}>
                <b>{labels.you_will_receive}</b> {currencySign} {StringHelper.toPresentableMoneyValue(amounts.finalAmount)}
            </span>
            <hr />
            <span>{labels.token_sent}</span>
            <FormControl scheme={Schemes.OMNEHOST} isFieldset fill label={labels.confirmation_token} style={{ marginTop: 15 }} >
                <TextInput fill onInputComplete={(v) => {
                    dialogErrorRef.current!.innerText = " ";
                    payload.value = v;
                }} />
            </FormControl>
            <span style={{ marginTop: 20 }}>
                {labels.token_not_received} <span className="link" onClick={sendConfirmationToken}>{labels.resend_token}</span>
            </span>
            <div ref={dialogErrorRef} className="error" style={{ alignSelf: "flex-start" }}> </div>
        </div>),
        onCancel: () => {
            dialog.destroy(() => InitiateWithdraw(currency, selectedPayoutMethod, memberPayoutMethods, onDone));
        },
        onConfirm: (button?: ButtonManageRef) => {
            dialogErrorRef.current!.innerText = " ";
            if (!payload.value || !payload.factor_id) {
                dialogErrorRef.current!.innerText = labels.enter_token;
                return false;
            }
            button?.setLoadingState(true);
            Debugger.log("EARNING.WITHDRAWAL", "payload to initiate withdrawal", payload);
            earningService.initiateWithdrawal(payload).then(({ }: SanitizedResponse<any>) => {
                dialog.hide(() => {
                    onDone?.();
                    const dialogLabels = i18nManager.Labels.earnings.withdrawal_settings.dialog.withdrawal_successful;
                    const sDialog = ConfirmDialog({
                        noControl: true,
                        title: dialogLabels.title,
                        footerElements: (<Button scheme={Schemes.OMNEHOST} text={i18nManager.Labels.common.close} style={{ width: "100%" }} onClick={() => sDialog.hide()} />),
                        desc: ObjectHelper.expandStringTemplate(dialogLabels.desc, {
                            currency: currencySign,
                            amount: StringHelper.toPresentableMoneyValue(amounts.amount),
                        }, { chop: "$" }),
                        content: (<div style={{ margin: 20, width: "100%", display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center" }}>
                            <i className={Classname.build("fa fa-check", Animation.ZOOM)}
                                style={{ fontSize: 120, borderRadius: "100%", padding: "25px 35px", color: "white", background: "var(--mainColor)", "--transitionTimeout": "3s" } as any} />
                        </div>),
                    }).show();
                });
            }).catch(({ errorMessage }) => {
                dialogErrorRef.current!.innerText = errorMessage;
            }).finally(() => {
                button?.setLoadingState(false);
            });
            return false;
        }
    });
    dialog.show(sendConfirmationToken);
}

export default Withdrawal;
